/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/alt-text */
import React, { useState, useEffect, useMemo, useRef, memo } from 'react'
import { Link } from 'react-router-dom'
import ClipLoader from 'react-spinners/ClipLoader'
import ClockIcon from '../icons/alarm.svg'
import UsersIcon from '../icons/users.svg'
import ThunderIcon from '../icons/thunder.svg'
import StarIcon from '../icons/star.svg'
import StarLineIcon from '../icons/star-line.svg'
import formatNumber from '../utils/constants.ts'
import buildBuyLink from '../utils/buildBuyLink.ts'
import TokenLogoImage from './TokenLogoImage.jsx'

// ---- watchlist helpers (duplicated to preserve original logic) ----
const wlKey = (address, chainId) => {
  const addr = String(address || '').toLowerCase()
  const c = chainId ?? ''
  return c === '' ? addr : `${addr}:${c}`
}
const isWatched = (wl, address, chainId) => {
  const addr = String(address || '').toLowerCase()
  if (!Array.isArray(wl) || !addr) return false
  const key = wlKey(addr, chainId)
  return wl.includes(key) || wl.includes(addr)
}

// Money: K/M/B with **2 fixed decimals** (duplicated to preserve rendering exactly)
const formatMoney1 = n => {
  const x = Number(n) || 0
  const abs = Math.abs(x)
  let v = x
  let s = ''
  if (abs >= 1e9) {
    v = x / 1e9
    s = 'B'
  } else if (abs >= 1e6) {
    v = x / 1e6
    s = 'M'
  } else if (abs >= 1e3) {
    v = x / 1e3
    s = 'K'
  }
  const str = Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
  return str + s
}

const TopTenList = memo(function TopTenList({
  showTop10,
  loadingEssential,
  loadingDeferred,
  timeFilterChadLists,
  chadLists,
  totalVol,
  totalMc,
  watchlist,
  toggleWatchlist,
  onTokenSwap,
  creating1,
  poolAddress,
  firstPaintDone,
  selectedItem,
  setSelectedItem,
  buyAmount,
  setBuyAmount
}) {
  // Space‑efficient Top 10 module

  // Skeleton hold to avoid flash
  const [top10SkelHoldDone, setTop10SkelHoldDone] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setTop10SkelHoldDone(true), 450)
    return () => clearTimeout(t)
  }, [])
  const showTop10Skeleton =
    !firstPaintDone || !top10SkelHoldDone || loadingEssential

  // Local menu state for time window selector
  const [twMenuOpen, setTwMenuOpen] = useState(false)
  const timeOptions = ['15m', '1H', '6H', '24H', '1W', '2W', '1M', 'All time']
  const twPopoverRef = useRef(null)
  const [twFocusIndex, setTwFocusIndex] = useState(() =>
    Math.max(
      0,
      Array.isArray(timeOptions) ? timeOptions.indexOf(selectedItem) : 0
    )
  )

  useEffect(() => {
    if (!twMenuOpen) return
    const onDocClick = e => {
      if (!twPopoverRef.current) return
      if (!twPopoverRef.current.contains(e.target)) setTwMenuOpen(false)
    }
    const onKey = e => {
      if (e.key === 'Escape') setTwMenuOpen(false)
      if (!twMenuOpen) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setTwFocusIndex(i => (i + 1) % timeOptions.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setTwFocusIndex(i => (i - 1 + timeOptions.length) % timeOptions.length)
      } else if (e.key === 'Enter') {
        const opt = timeOptions[twFocusIndex]
        if (opt) {
          setSelectedItem(opt)
          setTwMenuOpen(false)
        }
      }
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [twMenuOpen, timeOptions, twFocusIndex, setSelectedItem])

  // Collapsible: collapsed by default; remember preference
  const [expanded, setExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem('top10:expanded')
      return raw ? JSON.parse(raw) === true : false
    } catch {
      return false
    }
  })
  useEffect(() => {
    try {
      localStorage.setItem('top10:expanded', JSON.stringify(expanded))
    } catch {}
  }, [expanded])

  // Stable, minimal computations
  const topAllTime = useMemo(() => {
    if (loadingEssential) return []
    const base = Array.isArray(chadLists) ? chadLists : []
    return [...base].sort((a, b) => b.progress - a.progress).slice(0, 10)
  }, [chadLists, loadingEssential])

  const topWindow = useMemo(() => {
    if (loadingEssential) return []
    const base = Array.isArray(timeFilterChadLists) ? timeFilterChadLists : []
    return [...base].sort((a, b) => b.progress - a.progress).slice(0, 10)
  }, [timeFilterChadLists, loadingEssential])

  const listToShow =
    firstPaintDone &&
    !loadingEssential &&
    Array.isArray(topWindow) &&
    topWindow.length > 0
      ? topWindow
      : topAllTime

  const topCollapsed = Array.isArray(listToShow) ? listToShow.slice(0, 10) : []

  return showTop10 ? (
    <div className="w-full text-xs sm:text-[13px]">
      <div className="rounded-lg border border-[#2a2d36] bg-[#171a21] px-4 md:px-5 py-2">
        {/* Header */}
        <div className="flex items-center justify-between text-[12px] sm:text-[13px]">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="inline-block h-3 w-3 rounded-full bg-[#7CFC98] shadow-[0_0_8px_rgba(245,0,255,0.6)]"
              aria-hidden="true"
            />
            <div className="truncate">
              <div className="truncate text-[13px] leading-5 text-[#f8ffe8] font-semibold">
                top 10{' '}
                <span className="font-normal text-[#f8ffe8]/80">coins</span>
              </div>
              <div className="hidden sm:block text-[12px] leading-4 text-[#f8ffe8]/50 truncate">
                trending right now
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[#f8ffe8]/70">
            {/* time window selector */}
            <div className="relative" ref={twPopoverRef}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={twMenuOpen}
                onClick={() => setTwMenuOpen(v => !v)}
                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1 text-[#f8ffe8] transition-colors hover:translate-y-[-1px] ${
                  twMenuOpen
                    ? 'border-[#a135bb] bg-[#2a2d36] shadow-[0_0_0_2px_rgba(161,53,187,0.25)]'
                    : 'border-[#2a2d36] bg-[#2a2d36]/40 hover:bg-[#2a2d36] hover:border-[#a135bb]'
                }`}
              >
                <img src={ClockIcon} className="h-4 w-4 opacity-90" />
                <span className="font-semibold text-[12px]">
                  {selectedItem}
                </span>
                <span className="opacity-70">▾</span>
              </button>

              {twMenuOpen && (
                <div
                  role="menu"
                  aria-label="Select time window"
                  className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-[#2a2d36] bg-[#171a21] p-2 shadow-2xl"
                >
                  <div className="grid grid-cols-4 gap-2">
                    {timeOptions.map((opt, idx) => {
                      const active = opt === selectedItem
                      const focused = idx === twFocusIndex
                      return (
                        <button
                          key={opt}
                          role="menuitemradio"
                          aria-checked={active}
                          tabIndex={0}
                          onMouseEnter={() => setTwFocusIndex(idx)}
                          onClick={() => {
                            setSelectedItem(opt)
                            setTwMenuOpen(false)
                          }}
                          className={`px-2 py-1 rounded-lg border text-[12px] text-center transition-colors ${
                            active
                              ? 'border-[#a135bb] bg-[#2a2d36] text-[#f8ffe8]'
                              : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/80 hover:bg-[#2a2d36] hover:border-[#a135bb]'
                          } ${
                            focused
                              ? 'outline outline-1 outline-[#a135bb]/50'
                              : ''
                          }`}
                        >
                          {opt}
                        </button>
                      )
                    })}
                  </div>
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={() => setTwMenuOpen(false)}
                      className="inline-flex items-center h-7 px-3 rounded-lg border border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/90 hover:bg-[#2a2d36] text-[12px]"
                    >
                      close
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* expand/collapse */}
            <button
              onClick={() => setExpanded(e => !e)}
              aria-pressed={expanded}
              className={`ml-1 inline-flex items-center h-7 px-3 rounded-lg border text-[12px] transition-colors hover:translate-y-[-1px] ${
                expanded
                  ? 'border-[#a135bb] bg-[#2a2d36] text-[#f8ffe8]'
                  : 'border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb]'
              }`}
            >
              {expanded ? 'hide' : 'show'}
            </button>
          </div>
        </div>

        {/* Collapsed horizontal preview */}
        {!expanded &&
          firstPaintDone &&
          !loadingEssential &&
          topCollapsed.length > 0 && (
            <div className="mt-3">
              <div className="flex flex-wrap items-center gap-2">
                {topCollapsed.map((pool, index) => (
                  <Link
                    key={`${pool.contractAddress}:${pool.chainId}`}
                    to={buildBuyLink(
                      pool.chainId,
                      pool.contractAddress,
                      pool.versionType
                    )}
                    className="flex items-center gap-2 bg-[#22262e] hover:bg-[#2a2e38] border border-[#2a2d36] rounded-full px-2.5 py-1"
                    title={pool.tokenSymbol}
                  >
                    <span className="w-5 text-center text-white/80 text-[12px]">
                      {index + 1}
                    </span>
                    <TokenLogoImage
                      address={pool.contractAddress}
                      chainId={pool.chainId}
                      version={pool.versionType}
                      src={pool.logoUrl}
                      width={18}
                      height={18}
                      className="h-[18px] w-[18px] rounded-full"
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="text-[#f500ff] font-semibold text-[12px] max-w-[6rem] truncate">
                      {pool.tokenSymbol}
                    </span>
                    <span
                      className={`text-[11px] px-1.5 py-0.5 rounded tabular-nums ${
                        pool.isRising
                          ? 'text-green-400/90 bg-green-400/10'
                          : 'text-red-400/90 bg-red-400/10'
                      }`}
                    >
                      {Number.isFinite(pool.progress)
                        ? pool.progress.toFixed(1)
                        : 0}
                      %
                    </span>
                  </Link>
                ))}
                <button
                  onClick={() => setExpanded(true)}
                  className="ml-1 inline-flex items-center h-7 px-3 rounded-lg border border-[#2a2d36] bg-[#2a2d36]/40 text-[#f8ffe8]/90 hover:bg-[#2a2d36] hover:border-[#a135bb] transition-colors hover:translate-y-[-1px] text-[12px]"
                >
                  expand
                </button>
              </div>
            </div>
          )}
        {/* Collapsed skeleton (horizontal pills) */}
        {!expanded && showTop10Skeleton && (
          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-[#22262e] border border-[#2a2d36] rounded-full px-2.5 py-1 animate-pulse"
                >
                  <div className="w-5 text-center text-[#f8ffe8]/40 text-[12px]">
                    #
                  </div>
                  <div className="h-[18px] w-[18px] rounded-full bg-gray-700" />
                  <div className="h-3 w-16 bg-gray-700 rounded" />
                  <div className="h-4 w-10 bg-gray-700 rounded" />
                </div>
              ))}
              <div className="ml-1 px-2 py-1 rounded border border-[#a135bb]/50 text-[#f8ffe8]/40 text-[12px]">
                expand
              </div>
            </div>
          </div>
        )}

        {/* Skeleton */}
        {expanded && showTop10Skeleton && (
          <ul className="flex flex-col gap-y-[1px]">
            {Array.from({ length: 10 }).map((_, i) => (
              <li
                key={i}
                className="py-px animate-pulse flex items-center gap-2"
              >
                <div className="w-5 text-center text-[#f8ffe8]/40">#</div>
                <div className="h-5 w-5 rounded-full bg-gray-700" />
                <div className="flex-1 min-w-0">
                  <div className="h-3 w-24 bg-gray-700 rounded" />
                </div>
                <div className="hidden sm:block h-3 w-10 bg-gray-700 rounded" />
                <div className="hidden md:block h-3 w-16 bg-gray-700 rounded" />
                <div className="hidden md:block h-3 w-16 bg-gray-700 rounded" />
                <div className="h-5 w-5 bg-gray-700 rounded ml-2" />
                <div className="h-5 w-5 bg-gray-700 rounded" />
              </li>
            ))}
          </ul>
        )}

        {/* No‑data message for selected window */}
        {expanded &&
          !showTop10Skeleton &&
          timeFilterChadLists?.length === 0 &&
          Array.isArray(chadLists) &&
          chadLists.length > 0 && (
            <div className="py-1.5 text-center text-[#f8ffe8]/60">
              no data for this time period – showing all‑time instead
            </div>
          )}

        {/* Compact rows */}
        {expanded && !showTop10Skeleton && listToShow.length > 0 && (
          <>
            <ul className="flex flex-col gap-y-[1px]">
              {listToShow.map((pool, index) => (
                <li
                  key={`${pool.contractAddress}:${pool.chainId}`}
                  className={`py-px flex items-center gap-1 transition ${
                    index < 3
                      ? 'bg-gradient-to-r from-[#a135bb0a] to-transparent'
                      : ''
                  } hover:bg-white/5`}
                >
                  {/* Rank */}
                  <div className="w-5 text-center font-semibold text-white/90 text-[12px]">
                    {index === 0
                      ? '🥇'
                      : index === 1
                      ? '🥈'
                      : index === 2
                      ? '🥉'
                      : index + 1}
                  </div>

                  {/* Logo + symbol (truncate) */}
                  <Link
                    to={buildBuyLink(
                      pool.chainId,
                      pool.contractAddress,
                      pool.versionType
                    )}
                    className="flex items-center gap-1 min-w-0 flex-1"
                  >
                    <TokenLogoImage
                      address={pool.contractAddress}
                      chainId={pool.chainId}
                      version={pool.versionType}
                      src={pool.logoUrl}
                      alt="logo"
                      loading="lazy"
                      decoding="async"
                      width={28}
                      height={28}
                      className="h-5 w-5 rounded-full flex-none"
                      style={{
                        contentVisibility: 'auto',
                        containIntrinsicSize: '28px 28px'
                      }}
                    />
                    <span className="font-semibold text-[#f500ff] truncate max-w-[8rem] sm:max-w-[11rem] text-[12px]">
                      {pool.tokenSymbol}
                    </span>
                    <img
                      src={pool.blockchainLogoUrl}
                      alt="chain"
                      loading="lazy"
                      decoding="async"
                      width={14}
                      height={14}
                      className="h-3 w-3 rounded-full opacity-80"
                      style={{
                        contentVisibility: 'auto',
                        containIntrinsicSize: '14px 14px'
                      }}
                    />
                  </Link>

                  {/* Progress (chip + thin bar) */}
                  <div className="hidden sm:flex flex-col items-end min-w-[60px]">
                    <div
                      className={`inline-flex items-center px-px py-0 rounded text-[10px] tabular-nums ${
                        pool.isRising
                          ? 'text-green-400/90 bg-green-400/10'
                          : 'text-red-400/90 bg-red-400/10'
                      }`}
                    >
                      {Number.isFinite(pool.progress)
                        ? pool.progress.toFixed(1)
                        : 0}
                      %
                    </div>
                    <div className="mt-0.5 h-px w-10 bg-white/10 rounded overflow-hidden">
                      <div
                        className={`h-full ${
                          pool.isRising ? 'bg-green-400/60' : 'bg-red-400/60'
                        }`}
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(100, Number(pool.progress || 0))
                          )}%`
                        }}
                      />
                    </div>
                  </div>

                  {/* Holders */}
                  <div className="hidden md:flex items-center gap-1 text-white/70 tabular-nums min-w-[72px] justify-end text-[12px]">
                    <img
                      src={UsersIcon}
                      alt="holders"
                      className="w-3 h-3 opacity-80"
                      decoding="async"
                    />
                    <span>{formatNumber(pool.holdersNumber)}</span>
                  </div>

                  {/* MC */}
                  <div className="hidden md:block text-white/70 tabular-nums min-w-[84px] text-right text-[12px]">
                    ${formatMoney1(pool.marketCap)}
                  </div>

                  {/* Vol */}
                  <div className="hidden md:block text-white/70 tabular-nums min-w-[84px] text-right text-[12px]">
                    ${formatMoney1(pool.depositedAmount)}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 ml-2">
                    {/* star toggle (consistent composite logic) */}
                    <button
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        toggleWatchlist(pool.contractAddress, pool.chainId)
                      }}
                      className="ml-1"
                      type="button"
                      aria-label="Toggle favorite"
                    >
                      {(() => {
                        const isFav = isWatched(
                          watchlist,
                          pool.contractAddress,
                          pool.chainId
                        )
                        return (
                          <img
                            src={isFav ? StarIcon : StarLineIcon}
                            width={16}
                            className="w-5 h-5 opacity-80 hover:opacity-100 transition-opacity"
                            alt={isFav ? 'Unfavorite' : 'Favorite'}
                          />
                        )
                      })()}
                    </button>
                    <button
                      onClick={() =>
                        onTokenSwap(
                          pool.contractAddress,
                          pool.tokenAddress,
                          pool.chainId,
                          pool.tokenSymbol
                        )
                      }
                      disabled={creating1 || pool.lpCreated}
                      className="p-1 border border-[#a135bb]/60 rounded hover:bg-[#2c2c2c]"
                    >
                      {creating1 && pool.contractAddress === poolAddress ? (
                        <ClipLoader size={16} color="#a135bb" />
                      ) : (
                        <img
                          src={ThunderIcon}
                          alt="buy"
                          width={16}
                          decoding="async"
                          className="w-3 h-3"
                        />
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="pt-2 text-right">
              <button
                onClick={() => setExpanded(false)}
                className="text-[12px] text-[#f8ffe8]/70 hover:text-white"
              >
                hide top 10 ↑
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null
})

export default TopTenList
